import {useRef, useCallback, useEffect, type PointerEvent as ReactPointerEvent} from 'react'
import {Box} from '@mui/material'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Constants ─────────────────────────────────────────────────────

const PICKER_SIZE = 80
const HANDLE_RADIUS = 6
const TRACK_RADIUS = 32

// ── AnglePicker — circular drag widget for gradient angle ─────────

interface AnglePickerProps {
  value: number
  onChange: (angle: number) => void
  onDragEnd?: () => void
}

const AnglePicker = ({value, onChange, onDragEnd}: AnglePickerProps) => {
  const tokens = useAppTokens()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const draggingRef = useRef(false)

  // Convert pointer position to angle
  const getAngleFromEvent = useCallback((e: PointerEvent | ReactPointerEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return 0
    const rect = canvas.getBoundingClientRect()
    const cx = rect.width / 2
    const cy = rect.height / 2
    const dx = e.clientX - rect.left - cx
    const dy = e.clientY - rect.top - cy
    const rad = Math.atan2(dy, dx)
    const deg = ((rad * 180) / Math.PI + 90 + 360) % 360
    return Math.round(deg)
  }, [])

  const handlePointerDown = useCallback(
    (e: ReactPointerEvent) => {
      draggingRef.current = true
      onChange(getAngleFromEvent(e))
    },
    [onChange, getAngleFromEvent],
  )

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!draggingRef.current) return
      onChange(getAngleFromEvent(e))
    }
    const handlePointerUp = () => {
      if (draggingRef.current) onDragEnd?.()
      draggingRef.current = false
    }
    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [onChange, onDragEnd, getAngleFromEvent])

  // Draw the angle picker
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    canvas.width = PICKER_SIZE * dpr
    canvas.height = PICKER_SIZE * dpr
    ctx.scale(dpr, dpr)

    const cx = PICKER_SIZE / 2
    const cy = PICKER_SIZE / 2

    ctx.clearRect(0, 0, PICKER_SIZE, PICKER_SIZE)

    // Track circle
    ctx.beginPath()
    ctx.arc(cx, cy, TRACK_RADIUS, 0, Math.PI * 2)
    ctx.strokeStyle = tokens.accordion.border
    ctx.lineWidth = 2
    ctx.stroke()

    // Direction line
    const rad = ((value - 90) * Math.PI) / 180
    const hx = cx + Math.cos(rad) * TRACK_RADIUS
    const hy = cy + Math.sin(rad) * TRACK_RADIUS
    ctx.beginPath()
    ctx.moveTo(cx, cy)
    ctx.lineTo(hx, hy)
    ctx.strokeStyle = tokens.text.secondary
    ctx.lineWidth = 1.5
    ctx.stroke()

    // Handle dot
    ctx.beginPath()
    ctx.arc(hx, hy, HANDLE_RADIUS, 0, Math.PI * 2)
    ctx.fillStyle = tokens.text.primary
    ctx.fill()
  }, [value, tokens])

  return (
    <Box sx={buildStyles(tokens).wrapper}>
      <canvas
        ref={canvasRef}
        style={{width: PICKER_SIZE, height: PICKER_SIZE, cursor: 'pointer', touchAction: 'none'}}
        onPointerDown={handlePointerDown}
      />
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '8px',
    backgroundColor: tokens.sidebar.background,
  },
})

export default AnglePicker
